import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaEye, FaSave, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import { IoDocumentAttachOutline } from 'react-icons/io5';

const PrescriptionForm = () => {
  const { id, customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    customer: customerId || '',
    prescriptionDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    prescriptionType: 'glasses',
    doctorName: '',
    rightEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: '',
      pd: ''
    },
    leftEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: '',
      pd: ''
    },
    notes: ''
  });
  
  const [prescriptionImage, setPrescriptionImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const fileInputRef = useRef();

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };


  useEffect(() => {
    fetchCustomers();
    if (isEditMode) {
      fetchPrescription();
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
            const res = await api.get('/customers', {
        params: {
          limit: 100,
          sortField: 'name',
          sortOrder: 'asc'
        }
      });
      setCustomers(res.data.customers);
      setLoadingCustomers(false);
      console.log("res.data", res.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
      setLoadingCustomers(false);
    }
  };

  const fetchPrescription = async () => {
    try {
            const res = await api.get(`/prescriptions/${id}`);
      // console.log("res.data", res.data.data);
      // Format dates for form inputs
      const prescriptionDate = res.data.data.prescriptionDate ? 
        new Date(res.data.data.prescriptionDate).toISOString().split('T')[0] : '';
      
      const expiryDate = res.data.data.expiryDate ? 
        new Date(res.data.data.expiryDate).toISOString().split('T')[0] : '';
      
      setFormData({
        customer: res.data.data.customer._id,
        prescriptionDate,
        expiryDate,
        prescriptionType: res.data.data.prescriptionType || 'glasses',
        doctorName: res.data.data.doctor.name || '',
        rightEye: {
          sphere: res.data.data.prescriptionDetails.rightEye?.sphere || '',
          cylinder: res.data.data.prescriptionDetails.rightEye?.cylinder || '',
          axis: res.data.data.prescriptionDetails.rightEye?.axis || '',
          add: res.data.data.prescriptionDetails.rightEye?.add || '',
          pd: res.data.data.prescriptionDetails.rightEye?.pd || ''
        },
        leftEye: {
          sphere: res.data.data.prescriptionDetails.leftEye?.sphere || '',
          cylinder: res.data.data.prescriptionDetails.leftEye?.cylinder || '',
          axis: res.data.data.prescriptionDetails.leftEye?.axis || '',
          add: res.data.data.prescriptionDetails.leftEye?.add || '',
          pd: res.data.data.prescriptionDetails.leftEye?.pd || ''
        },
        notes: res.data.data.notes || ''
      });
      
      if (res.data.data.prescriptionImage) {
        setImagePreview(`${api.defaults.baseURL}/prescriptions/download/${id}`);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prescription:', err);
      setError('Failed to load prescription data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('rightEye.') || name.includes('leftEye.')) {
      const [eye, field] = name.split('.');
      setFormData({
        ...formData,
        [eye]: {
          ...formData[eye],
          [field]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrescriptionImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.customer) {
      errors.customer = 'Customer is required';
    }
    
    if (!formData.prescriptionDate) {
      errors.prescriptionDate = 'Prescription date is required';
    }
    
    if (!formData.doctorName.trim()) {
      errors.doctorName = 'Doctor name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData object for file upload
      const formDataToSend = new FormData();
      
      // Append all form fields
      formDataToSend.append('customer', formData.customer);
      formDataToSend.append('prescriptionDate', formData.prescriptionDate);
      if (formData.expiryDate) {
        formDataToSend.append('expiryDate', formData.expiryDate);
      }
      formDataToSend.append('prescriptionType', formData.prescriptionType);
      formDataToSend.append('doctorName', formData.doctorName);
      formDataToSend.append('notes', formData.notes);
      
      // Append eye details as JSON strings
      formDataToSend.append('rightEye', JSON.stringify(formData.rightEye));
      formDataToSend.append('leftEye', JSON.stringify(formData.leftEye));
      
      // Append file if selected
      if (prescriptionImage) {
        formDataToSend.append('prescriptionImage', prescriptionImage);
      }
      
      if (isEditMode) {
                await api.put(`/prescriptions/${id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Prescription updated successfully');
      } else {
                const res = await api.post('/prescriptions', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Prescription added successfully');
        navigate(`/prescriptions/${res.data.data._id}`);
      }
      
      if (isEditMode) {
        navigate(`/prescriptions/${id}`);
      }
    } catch (err) {
      console.error('Error saving prescription:', err);
      setError(err.response?.data?.msg || 'Failed to save prescription');
      toast.error(err.response?.data?.msg || 'Failed to save prescription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please upload a valid PDF file');
      return;
    }
  
    const formData = new FormData();
    formData.append('pdf', file);
  
    try {
            const res = await api.post('/prescriptions/extract-from-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
  
      const data = res.data.data;
      setFormData((prev) => ({
        ...prev,
        ...data,
        rightEye: { ...prev.rightEye, ...data.rightEye },
        leftEye: { ...prev.leftEye, ...data.leftEye }
      }));
  
      toast.success('Prescription data auto-filled from PDF');
    } catch (err) {
      toast.error('Failed to extract data from PDF');
    }
    e.target.value = null;
  };
  
  

  if (loading || loadingCustomers) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>
            <FaEye className="me-2" />
            {isEditMode ? 'Edit Prescription' : 'Add New Prescription'}
          </h1>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
        <Card.Header className="bg-light border-bottom">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
        <div className="fw-semibold fs-5 mb-2 mb-md-0">Basic Information</div>

        <div>
          <input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            onChange={handlePdfUpload}
            style={{ display: 'none' }}
          />
          <Button variant="primary" size="sm" onClick={handleButtonClick}>
            <IoDocumentAttachOutline className="me-2" />
            Upload Prescription PDF
          </Button>
        </div>
      </div>
    </Card.Header>
  
         

          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="customer">
                  <Form.Label>Customer <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="customer"
                    value={formData.customer}
                    onChange={handleChange}
                    isInvalid={!!formErrors.customer}
                    disabled={!!customerId}
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer._id} value={customer._id}>
                        {customer.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.customer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="doctorName">
                  <Form.Label>Doctor Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="doctorName"
                    value={formData.doctorName}
                    onChange={handleChange}
                    placeholder="Enter doctor's name"
                    isInvalid={!!formErrors.doctorName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.doctorName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="prescriptionDate">
                  <Form.Label>Prescription Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="prescriptionDate"
                    value={formData.prescriptionDate}
                    onChange={handleChange}
                    isInvalid={!!formErrors.prescriptionDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.prescriptionDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3" controlId="expiryDate">
                  <Form.Label>Expiry Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3" controlId="prescriptionType">
                  <Form.Label>Prescription Type</Form.Label>
                  <Form.Select
                    name="prescriptionType"
                    value={formData.prescriptionType}
                    onChange={handleChange}
                  >
                    <option value="glasses">Glasses</option>
                    <option value="contact_lenses">Contact Lenses</option>
                    <option value="both">Both</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Prescription Details</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h5 className="mb-3">Right Eye</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.sphere">
                      <Form.Label>Sphere (SPH)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.sphere"
                        value={formData.rightEye.sphere}
                        onChange={handleChange}
                        placeholder="Enter sphere value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.cylinder">
                      <Form.Label>Cylinder (CYL)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.cylinder"
                        value={formData.rightEye.cylinder}
                        onChange={handleChange}
                        placeholder="Enter cylinder value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.axis">
                      <Form.Label>Axis</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.axis"
                        value={formData.rightEye.axis}
                        onChange={handleChange}
                        placeholder="Enter axis value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.add">
                      <Form.Label>Add</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.add"
                        value={formData.rightEye.add}
                        onChange={handleChange}
                        placeholder="Enter add value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.pd">
                      <Form.Label>PD (Pupillary Distance)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.pd"
                        value={formData.rightEye.pd}
                        onChange={handleChange}
                        placeholder="Enter PD value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              
              <Col md={6}>
                <h5 className="mb-3">Left Eye</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.sphere">
                      <Form.Label>Sphere (SPH)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.sphere"
                        value={formData.leftEye.sphere}
                        onChange={handleChange}
                        placeholder="Enter sphere value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.cylinder">
                      <Form.Label>Cylinder (CYL)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.cylinder"
                        value={formData.leftEye.cylinder}
                        onChange={handleChange}
                        placeholder="Enter cylinder value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.axis">
                      <Form.Label>Axis</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.axis"
                        value={formData.leftEye.axis}
                        onChange={handleChange}
                        placeholder="Enter axis value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.add">
                      <Form.Label>Add</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.add"
                        value={formData.leftEye.add}
                        onChange={handleChange}
                        placeholder="Enter add value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.pd">
                      <Form.Label>PD (Pupillary Distance)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.pd"
                        value={formData.leftEye.pd}
                        onChange={handleChange}
                        placeholder="Enter PD value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Enter any additional notes about the prescription"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Prescription Image</Card.Header>
          <Card.Body>
            <Form.Group controlId="prescriptionImage">
              <Form.Label>Upload Prescription Image/PDF</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
              />
              <Form.Text className="text-muted">
                Upload a scanned copy or photo of the prescription (PDF, JPG, PNG)
              </Form.Text>
            </Form.Group>
            
            {imagePreview && (
              <div className="mt-3">
                <h6>Preview:</h6>
                {imagePreview.endsWith('.pdf') || imagePreview.includes('/download') ? (
                  <p>
                    <a href={imagePreview} target="_blank" rel="noopener noreferrer">
                      View PDF Prescription
                    </a>
                  </p>
                ) : (
                  <img 
                    src={imagePreview} 
                    alt="Prescription Preview" 
                    style={{ maxWidth: '100%', maxHeight: '300px' }} 
                  />
                )}
              </div>
            )}
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-end mb-4">
          <Button 
            variant="secondary" 
            as={Link} 
            to={isEditMode ? `/prescriptions/${id}` : '/prescriptions'} 
            className="me-2"
          >
            <FaTimes className="me-1" /> Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={isSubmitting}
          >
            <FaSave className="me-1" /> {isSubmitting ? 'Saving...' : 'Save Prescription'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default PrescriptionForm;
